// Story 1.8 (FR-4/ARCH-14, UX-DR36-38/55, SYS-UX-01, R-11): единственный router
// FreeHub shell.
//
// Модуль намеренно **чистый**: разбор URL, сериализация canonical форм и
// allowlist-состояние истории. DOM wiring, fetch/mutation owners и session
// lifecycle остаются в `app.js` (второй store/второй writer не создаётся), а
// `return-target.js` остаётся владельцем safe return target.
//
// Границы безопасности:
//   * canonical форма — только `/` (Documents), `/?section=<safe enum>`,
//     `/wizard/` и `/wizard/?mode=edit&documentId=<id>`, `/login` (auth surface,
//     не цель возврата). Любой legacy-вариант разрешается в ту же форму;
//   * `history.state` — allowlist (route/section/mode/documentId/действительно
//     безопасные enum/page). Пароли, question/retrievalQuery, заголовок
//     документа, свободный поиск и произвольный текст туда не сериализуются;
//   * неизвестные query-параметры не переезжают в canonical адрес: legacy-ссылка
//     нормализуется, а не «санитизируется» с сохранением остатка.

export const ROUTE_SECTION = "section";
export const ROUTE_WIZARD = "wizard";
export const ROUTE_LOGIN = "login";

export const SECTION_DOCUMENTS = "documents";
export const SECTIONS = Object.freeze([
  SECTION_DOCUMENTS,
  "agents",
  "evaluation",
  "logs",
  "system",
]);

export const WIZARD_MODE_CREATE = "create";
export const WIZARD_MODE_EDIT = "edit";
export const WIZARD_MODES = Object.freeze([WIZARD_MODE_CREATE, WIZARD_MODE_EDIT]);

export const DOCUMENTS_PATH = "/";
export const WIZARD_PATH = "/wizard";
export const WIZARD_CANONICAL_PATH = "/wizard/";
export const LOGIN_PATH = "/login";

export const SECTION_QUERY_KEY = "section";
export const MODE_QUERY_KEY = "mode";
export const DOCUMENT_ID_QUERY_KEY = "documentId";

/** Ключ allowlist-состояния истории (единственный ключ, который пишет router). */
export const HISTORY_STATE_KEY = "freehub.route.v1";

export const PAGE_MIN = 1;
export const PAGE_MAX = 1000;

/** Безопасные значения routine-фильтра документов (существующие status enums). */
export const SAFE_FILTERS = Object.freeze([
  "all",
  "completed",
  "processing",
  "failed",
  "registered",
]);

const DOCUMENT_ID_MAX_LENGTH = 128;
const DOCUMENT_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]*$/;

function locationParts(location) {
  const pathname = location && typeof location.pathname === "string" ? location.pathname : "";
  const search = location && typeof location.search === "string" ? location.search : "";
  return { pathname, search };
}

function normalizePath(pathname) {
  let path = pathname === "" ? DOCUMENTS_PATH : pathname;
  if (!path.startsWith("/")) path = `/${path}`;
  // `/wizard//` и `/wizard/` — одно и то же route; повторные слэши не создают
  // вторую форму адреса.
  while (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return path;
}

function searchParams(search) {
  // `URLSearchParams` терпим к отсутствию `?`; неразбираемый ввод даёт пустой набор.
  try {
    return new URLSearchParams(search || "");
  } catch (_error) {
    return new URLSearchParams("");
  }
}

export function isSafeSection(value) {
  return typeof value === "string" && SECTIONS.includes(value);
}

export function isSafeDocumentId(value) {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= DOCUMENT_ID_MAX_LENGTH &&
    DOCUMENT_ID_PATTERN.test(value)
  );
}

export function isSafeFilter(value) {
  return typeof value === "string" && SAFE_FILTERS.includes(value);
}

export function isSafePage(value) {
  return Number.isInteger(value) && value >= PAGE_MIN && value <= PAGE_MAX;
}

/** Canonical адрес раздела: Documents — `/`, остальные — `/?section=<enum>`. */
export function serializeSection(section) {
  const safe = isSafeSection(section) ? section : SECTION_DOCUMENTS;
  return safe === SECTION_DOCUMENTS ? DOCUMENTS_PATH : `${DOCUMENTS_PATH}?${SECTION_QUERY_KEY}=${safe}`;
}

/** Route-объект раздела (единственная форма для push/replace/state). */
export function sectionRoute(section) {
  const safe = isSafeSection(section) ? section : SECTION_DOCUMENTS;
  return {
    name: ROUTE_SECTION,
    section: safe,
    mode: "",
    documentId: "",
    canonical: serializeSection(safe),
  };
}

/** Route-объект мастера: `create` — без параметров, `edit` — только с валидным id. */
export function wizardRoute(mode, documentId) {
  const edit = mode === WIZARD_MODE_EDIT && isSafeDocumentId(documentId);
  return {
    name: ROUTE_WIZARD,
    section: SECTION_DOCUMENTS,
    mode: edit ? WIZARD_MODE_EDIT : WIZARD_MODE_CREATE,
    documentId: edit ? documentId : "",
    canonical: edit
      ? `${WIZARD_CANONICAL_PATH}?${MODE_QUERY_KEY}=${WIZARD_MODE_EDIT}&${DOCUMENT_ID_QUERY_KEY}=${encodeURIComponent(documentId)}`
      : WIZARD_CANONICAL_PATH,
  };
}

export function loginRoute() {
  return { name: ROUTE_LOGIN, section: "", mode: "", documentId: "", canonical: LOGIN_PATH };
}

/**
 * Разбирает адрес (initial URL или popstate) в canonical route.
 *
 * Legacy-матрица, наблюдаемая в существующих href/tests:
 *   `/`, `/?section=documents|agents|evaluation|logs|system`, legacy
 *   `/?section=documents`, `/wizard`, `/wizard/`, `/wizard?mode=edit&documentId=<id>`,
 *   `/wizard/?mode=edit&documentId=<id>`, `/login`.
 * Любой иной путь или неизвестный section нормализуется к документам, а не
 * открывает чужой раздел.
 */
export function parseRoute(location) {
  const { pathname, search } = locationParts(location);
  const path = normalizePath(pathname);

  if (path === LOGIN_PATH) return loginRoute();

  if (path === WIZARD_PATH) {
    const params = searchParams(search);
    const edit =
      params.get(MODE_QUERY_KEY) === WIZARD_MODE_EDIT &&
      isSafeDocumentId(params.get(DOCUMENT_ID_QUERY_KEY));
    return edit
      ? wizardRoute(WIZARD_MODE_EDIT, params.get(DOCUMENT_ID_QUERY_KEY))
      : wizardRoute(WIZARD_MODE_CREATE, "");
  }

  const requested = searchParams(search).get(SECTION_QUERY_KEY);
  return sectionRoute(isSafeSection(requested) ? requested : SECTION_DOCUMENTS);
}

/** Same-origin relative target (safe return target) → canonical route. */
export function parseRouteFromTarget(target) {
  if (typeof target !== "string" || target.length === 0) {
    return sectionRoute(SECTION_DOCUMENTS);
  }
  const withoutFragment = target.split("#")[0];
  const [pathname, ...rest] = withoutFragment.split("?");
  return parseRoute({
    pathname: pathname || DOCUMENTS_PATH,
    search: rest.length > 0 ? `?${rest.join("?")}` : "",
  });
}

/**
 * Allowlist-состояние начальной записи истории (reload/direct-entry).
 *
 * Значения `history.state` применяются только тогда, когда адрес текущей записи
 * — canonical форма этого route: тогда запись принадлежит тому же переходу этой
 * же сессии и её безопасные enum/page восстанавливаются. В любом другом случае
 * возвращается детерминированный пустой набор — прежняя запись (или завершённая
 * сессия) своё состояние не наследует.
 */
export function initialRouteState(route, location, rawState) {
  const { pathname, search } = locationParts(location);
  if (`${pathname}${search}` !== serializeRoute(route)) {
    return readHistoryState(null);
  }
  return readHistoryState(rawState);
}

export function serializeRoute(route) {
  if (!route || typeof route !== "object") return DOCUMENTS_PATH;
  if (route.name === ROUTE_LOGIN) return LOGIN_PATH;
  if (route.name === ROUTE_WIZARD) return wizardRoute(route.mode, route.documentId).canonical;
  return serializeSection(route.section);
}

export function sameRoute(a, b) {
  if (!a || !b) return false;
  return (
    a.name === b.name &&
    (a.section || "") === (b.section || "") &&
    (a.mode || "") === (b.mode || "") &&
    (a.documentId || "") === (b.documentId || "")
  );
}

/**
 * Allowlist-состояние для `history.state`: записываются только перечисленные
 * безопасные поля. Свободный текст, question/retrievalQuery, секреты и заголовок
 * документа сюда попасть не могут — их просто нет в форме.
 */
export function routeHistoryState(route, state = {}) {
  const payload = { route: typeof route?.name === "string" ? route.name : "" };
  if (route?.name === ROUTE_SECTION) {
    payload.section = isSafeSection(route.section) ? route.section : SECTION_DOCUMENTS;
    if (isSafeFilter(state.filter)) payload.filter = state.filter;
    if (isSafePage(state.page)) payload.page = state.page;
  }
  if (route?.name === ROUTE_WIZARD) {
    const mode = route.mode === WIZARD_MODE_EDIT ? WIZARD_MODE_EDIT : WIZARD_MODE_CREATE;
    payload.mode = mode;
    if (mode === WIZARD_MODE_EDIT && isSafeDocumentId(route.documentId)) {
      payload.documentId = route.documentId;
    }
  }
  return { [HISTORY_STATE_KEY]: payload };
}

/** Читает allowlist-состояние; небезопасные/чужие значения отбрасываются целиком. */
export function readHistoryState(rawState) {
  const payload =
    rawState && typeof rawState === "object" ? rawState[HISTORY_STATE_KEY] : null;
  if (!payload || typeof payload !== "object") return { section: "", filter: "", page: 0 };
  return {
    section: isSafeSection(payload.section) ? payload.section : "",
    filter: isSafeFilter(payload.filter) ? payload.filter : "",
    page: isSafePage(payload.page) ? payload.page : 0,
  };
}

/**
 * Подпись «route + безопасное состояние»: повторное применение той же подписи не
 * переигрывает загрузку/mutation (одно route application на переход).
 */
export function routeSignature(route, state = {}) {
  const filter = isSafeFilter(state.filter) ? state.filter : "";
  const page = isSafePage(state.page) ? state.page : "";
  return [
    route?.name || "",
    route?.section || "",
    route?.mode || "",
    route?.documentId || "",
    filter,
    page,
  ].join("|");
}
